import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

import type { Document, GradeLevel } from "@markeroo/shared";
import {
  Editor,
  type EditorRef,
  jsonContentToProseMirror,
  proseMirrorToJsonContent,
  createEmptyDoc,
  createPasteStripPlugin,
  type JSONContent,
} from "../editor/unified";
import { useStudents, useCreateStudent, useClasses } from "../../api/hooks";
import { fieldLabelClass, mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../../components/Modal";
import { StudentFormModal } from "../classes/components/StudentFormModal";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useNotification } from "../../providers/NotificationProvider";
import { useAnalytics } from "../../providers/AnalyticsProvider";
import { Tooltip } from "@/components/Tooltip";
import { notifications, ui, placeholders } from "@/strings";
import type { NotificationKind } from "../../providers/NotificationProvider";

const CREATE_NEW_STUDENT_VALUE = "__create_new__";

function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) {
    return 0;
  }
  return trimmed.split(/\s+/u).length;
}

function coerceInitialContent(document?: Document | null): ProseMirrorNode {
  if (document?.bodyJson) {
    // Type assertion via unknown: API returns Record<string, unknown> from Zod validation,
    // but we know it's JSONContent at runtime. TypeScript requires the double assertion.
    const node = jsonContentToProseMirror(document.bodyJson as unknown as JSONContent);
    if (node) {
      return node;
    }
  }
  if (document?.bodyText) {
    // Create a document with the text content
    const json: JSONContent = {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [
            {
              type: "text",
              text: document.bodyText,
            },
          ],
        },
      ],
    };
    const node = jsonContentToProseMirror(json);
    if (node) {
      return node;
    }
  }
  return createEmptyDoc();
}

export type PasteDocumentModalSubmitPayload = {
  studentId: string;
  bodyJson: JSONContent;
  status: "draft" | "final";
  shouldContinue: boolean;
  autoQueue: boolean;
};

export interface PasteDocumentModalProps {
  isOpen: boolean;
  assignmentName: string;
  classId: string;
  defaultDocumentStatus: "draft" | "final";
  initialDocument?: Document | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (payload: PasteDocumentModalSubmitPayload) => Promise<void>;
  /** Pre-selected student ID (e.g., from "Add paper" button on student card) */
  preselectedStudentId?: string | null;
}

const WORD_LIMIT = 5000;

// Memoize the paste strip plugin to avoid recreation
const pasteStripPlugin = createPasteStripPlugin();

export function PasteDocumentModal({
  isOpen,
  assignmentName,
  classId,
  defaultDocumentStatus,
  initialDocument,
  isSubmitting,
  onClose,
  onSubmit,
  preselectedStudentId,
}: PasteDocumentModalProps) {
  const studentsQuery = useStudents(classId);
  const studentsData = studentsQuery.data;
  const createStudentMutation = useCreateStudent(classId);
  const { show } = useNotification();
  const { track } = useAnalytics();
  const { data: classesData } = useClasses();
  const currentClass = classesData?.items.find((c) => c.id === classId);
  const [studentId, setStudentId] = useState(initialDocument?.studentId ?? "");
  const [status, setStatus] = useState<"draft" | "final">(initialDocument?.status ?? defaultDocumentStatus);
  const [error, setError] = useState<string | null>(null);
  const [wordCount, setWordCount] = useState(0);
  const [isCreateStudentModalOpen, setCreateStudentModalOpen] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const [autoQueue, setAutoQueue] = useState(true);
  const [prefilledStudentName, setPrefilledStudentName] = useState("");
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  const editorRef = useRef<EditorRef>(null);
  const [initialContent, setInitialContent] = useState<ProseMirrorNode>(() =>
    coerceInitialContent(initialDocument)
  );

  // Handle document changes
  const handleChange = useCallback((doc: ProseMirrorNode) => {
    // Use textBetween to add newlines between block nodes for proper word counting
    const text = doc.textBetween(0, doc.content.size, "\n");
    setWordCount(countWords(text));
  }, []);

  const resetFormFields = useCallback(() => {
    setStudentId("");
    setStatus(defaultDocumentStatus);
    setError(null);
    const emptyDoc = createEmptyDoc();
    setInitialContent(emptyDoc);
    editorRef.current?.setContent(emptyDoc);
    editorRef.current?.focus();
    setWordCount(0);
  }, [defaultDocumentStatus]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Priority: initialDocument > preselectedStudentId > empty
    setStudentId(initialDocument?.studentId ?? preselectedStudentId ?? "");
    setStatus(initialDocument?.status ?? defaultDocumentStatus);
    setError(null);
    setAddAnother(false);
    const content = coerceInitialContent(initialDocument);
    setInitialContent(content);
    // Use textBetween to add newlines between block nodes for proper word counting
    setWordCount(countWords(content.textBetween(0, content.content.size, "\n")));
  }, [initialDocument, isOpen, defaultDocumentStatus, preselectedStudentId]);

  const disabled =
    !isOpen ||
    isSubmitting ||
    !studentId ||
    wordCount === 0 ||
    wordCount > WORD_LIMIT;

  const helperText = useMemo(() => {
    if (!isOpen) {
      return null;
    }
    const remaining = WORD_LIMIT - wordCount;
    return ui.labels.wordCount(wordCount, remaining);
  }, [wordCount, isOpen]);

  const handleOpenCreateStudentModal = () => {
    setCreateStudentModalOpen(true);
  };

  const handleCloseCreateStudentModal = () => {
    setCreateStudentModalOpen(false);
  };

  const handleCreateStudent = (data: {
    name: string;
    elaGradeLevel?: GradeLevel;
    notes: string;
    isEll: boolean;
  }) => {
    createStudentMutation.mutate(data, {
      onSuccess: (newStudent) => {
        show(notifications.students.created, TOAST_SUCCESS);
        track("student_created_from_paper_modal");
        setStudentId(newStudent.id);
        setCreateStudentModalOpen(false);
        setPrefilledStudentName("");
        // Focus the paper editor after modal closes
        setTimeout(() => {
          editorRef.current?.focus();
        }, 100);
        // Refetch students in the background to update the list
        void studentsQuery.refetch();
      },
      onError: () => {
        show(notifications.students.createFailed, TOAST_ERROR);
        track("student_create_from_paper_modal_failed");
      },
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const editor = editorRef.current;
    if (!editor) {
      setError(notifications.documents.editorNotReady);
      return;
    }
    if (!studentId) {
      setError(notifications.documents.studentRequired);
      return;
    }
    if (wordCount === 0) {
      setError(notifications.documents.contentRequired);
      return;
    }
    if (wordCount > WORD_LIMIT) {
      setError(notifications.documents.wordLimitExceeded);
      return;
    }

    setError(null);
    const doc = editor.getContent();
    const json = proseMirrorToJsonContent(doc);
    const shouldContinue = addAnother && !initialDocument;
    void onSubmit({ studentId, bodyJson: json, status, shouldContinue, autoQueue })
      .then(() => {
        if (shouldContinue) {
          resetFormFields();
        }
      })
      .catch((submitError) => {
        setError(submitError instanceof Error ? submitError.message : notifications.documents.saveFailed);
      });
  };

  // Memoize plugins array to avoid recreation
  const plugins = useMemo(() => [pasteStripPlugin], []);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" aria-label={ui.actions.addPaper} data-tutorial-target="add-paper-modal">
        <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
          <ModalHeader
            title={
              <div>
                <div className="text-2xl font-bold text-brand-950">
                  {initialDocument ? `${ui.actions.edit} ${ui.labels.paper}` : ui.actions.addPaper}
                </div>
                <p className={`${mutedText} text-base font-normal mt-1`}>
                  {/* eslint-disable-next-line no-restricted-syntax -- colon punctuation between label and value */}
                  {ui.labels.assignment}: {assignmentName}
                </p>
              </div>
            }
            onClose={onClose}
          />
          <ModalBody>
            <label className={fieldLabelClass}>
              {ui.labels.student}
              <SearchableSelect
                value={studentId}
                onChange={setStudentId}
                options={
                  studentsData?.items
                    .filter((student) => !student.archived)
                    .map((student) => ({
                      value: student.id,
                      label: student.name,
                    })) ?? []
                }
                placeholder={placeholders.documents.selectStudent}
                createNewOption={{
                  label: ui.labels.createNewStudentOption,
                  value: CREATE_NEW_STUDENT_VALUE,
                  onSelect: handleOpenCreateStudentModal,
                }}
                onCreateWithName={(name) => {
                  setPrefilledStudentName(name);
                  handleOpenCreateStudentModal();
                }}
                required
                data-tutorial-target="student-select"
              />
            </label>

            <div>
              <label className={fieldLabelClass}>
                <span className="flex items-center gap-1">
                  {ui.labels.status}
                  <Tooltip content={ui.labels.statusHelp} />
                </span>
              </label>
              <div className="flex gap-4 mt-2" data-tutorial-target="status-radios">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="draft"
                    checked={status === "draft"}
                    onChange={(e) => setStatus(e.target.value as "draft" | "final")}
                    className="cursor-pointer"
                  />
                  <span className="text-theme-secondary">{ui.labels.draft}</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value="final"
                    checked={status === "final"}
                    onChange={(e) => setStatus(e.target.value as "draft" | "final")}
                    className="cursor-pointer"
                  />
                  <span className="text-theme-secondary">{ui.labels.final}</span>
                </label>
              </div>
            </div>

            <div
              className={`min-h-[130px] flex-1 overflow-y-auto rounded-xl border bg-surface-secondary p-4 cursor-text ${
                wordCount > WORD_LIMIT ? "border-semantic-warning" : "border"
              }`}
              data-error={wordCount > WORD_LIMIT}
              data-tutorial-target="paper-editor"
              onClick={() => editorRef.current?.focus()}
            >
              <Editor
                ref={editorRef}
                initialContent={initialContent}
                editable={true}
                plugins={plugins}
                onChange={handleChange}
                placeholder={placeholders.documents.pasteContent}
                autoFocus={true}
                ariaLabel="Document body"
                className="min-h-[130px] space-y-4 text-base leading-6 text-theme-primary"
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <div className="flex flex-wrap items-center justify-between gap-3 w-full">
              <div className={`${mutedText} text-sm`} aria-live="polite">
                {helperText}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-4 w-full">
                <div className="flex flex-wrap items-center gap-4">
                  {!initialDocument && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addAnother}
                        onChange={(e) => setAddAnother(e.target.checked)}
                        disabled={isSubmitting}
                        className="cursor-pointer"
                      />
                      <span className="text-sm text-theme-secondary">{ui.labels.addAnotherPaperAfter}</span>
                    </label>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer" data-tutorial-target="auto-queue-checkbox">
                    <input
                      type="checkbox"
                      checked={autoQueue}
                      onChange={(e) => setAutoQueue(e.target.checked)}
                      disabled={isSubmitting}
                      className="cursor-pointer"
                    />
                    <span className="text-sm text-theme-secondary">{ui.labels.autoQueueHelp}</span>
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  {error ? (
                    <p className="text-sm text-semantic-error" role="alert">
                      {error}
                    </p>
                  ) : null}
                  <button className={buttonVariants.primary} type="submit" disabled={disabled} data-tutorial-target="save-paper-button">
                    {isSubmitting ? ui.actions.saving : ui.actions.save}
                  </button>
                </div>
              </div>
            </div>
          </ModalFooter>
        </form>
      </Modal>

      <StudentFormModal
        isOpen={isCreateStudentModalOpen}
        onClose={() => {
          handleCloseCreateStudentModal();
          setPrefilledStudentName("");
        }}
        onSubmit={handleCreateStudent}
        isSubmitting={createStudentMutation.isPending}
        defaultElaGradeLevel={currentClass?.defaultElaGradeLevel ?? undefined}
        initialName={prefilledStudentName}
      />
    </>
  );
}
